// MARK://dhk8/admin-shell/pages/modules/UserDetailPage
// Ficha "ver-primero" de un usuario / miembro del personal (Gustavo 2026-06-23).
// Header con inicial + nombre + correo/rol/ID-de-POS + pills (estado, sin-acceso-admin)
// + KPIs (rol, ID de POS) + acciones de ítem (Editar, Reiniciar PIN, Activar/Suspender,
// Eliminar) + Tabs (General, Roles/Permisos, Historial). El tab General se ve primero;
// "Editar" abre el SectionedSheet con el MISMO formulario que el modal de edición de la
// lista (nombre/correo/rol/estado) → users.updateUser. El tab Roles/Permisos resuelve el
// rol asignado vía roles.listRoles (match por nombre) y muestra, en SOLO LECTURA, los
// MÓDULOS que ese rol deja ver — espejo del catálogo @dockethub/contracts (como RolesPage).
// Carga el usuario por id con users.getUser; la lista de roles asignables alimenta tanto
// el dropdown de edición como el tab de permisos.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Trash2, KeyRound, Copy, Power, ShieldCheck } from 'lucide-react'
import { MODULES, type AppKey } from '@dockethub/contracts/shared'

import * as users from '../../lib/clients/users.js'
import type { StaffUser, StaffRole, StaffStatus } from '../../lib/clients/users.js'
import * as rolesClient from '../../lib/clients/roles.js'
import type { Role } from '../../lib/clients/roles.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import Sheet from '../../components/Sheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'roles' | 'historial'

const STATUS_LABEL: Record<StaffStatus, string> = { ACTIVE: 'Activo', SUSPENDED: 'Suspendido', DELETED: 'Eliminado' }
const STATUS_TONE: Record<StaffStatus, StatusTone> = { ACTIVE: 'ok', SUSPENDED: 'danger', DELETED: 'neutral' }

// Etiqueta amistosa de un rol para el dropdown de asignación: los built-ins
// conservan su nombre es-MX (STAFF_ROLE_LABEL); los custom usan su descripción o
// el nombre Title-cased. (Mismo criterio que UsersPage / RolesPage.)
function roleOptionLabel(r: Role): string {
  const builtin = users.STAFF_ROLE_LABEL[r.name as StaffRole]
  if (builtin) return builtin
  if (r.description) return r.description
  return r.name.charAt(0).toUpperCase() + r.name.slice(1)
}

// Agrupa el catálogo igual que RolesPage: primero los módulos del Admin, luego los
// del POS, ordenados por etiqueta. (Un módulo puede estar en ambas apps.)
const MODULE_GROUPS: { app: AppKey; label: string; mods: typeof MODULES[number][] }[] = [
  { app: 'admin', label: 'En el Admin', mods: MODULES.filter((m) => m.apps.includes('admin')).slice().sort((a, b) => a.label.localeCompare(b.label, 'es')) },
  { app: 'pos', label: 'En el POS', mods: MODULES.filter((m) => m.apps.includes('pos')).slice().sort((a, b) => a.label.localeCompare(b.label, 'es')) },
]

export default function UserDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const user = useAsync((s) => users.getUser(id, s), [id])
  // Roles asignables: built-in + custom, en vivo. Alimenta el <select> de edición y el
  // tab Roles/Permisos (para resolver qué módulos ve el rol del usuario).
  const rolesList = useAsync((s) => rolesClient.listRoles(s), [])
  const assignableRoles: { name: string; label: string }[] =
    rolesList.data?.map((r) => ({ name: r.name, label: roleOptionLabel(r) }))
    ?? users.STAFF_ROLES.map((r) => ({ name: r, label: users.STAFF_ROLE_LABEL[r] }))

  const [tab, setTab] = useState<Tab>('general')

  // edit
  const [editing, setEditing] = useState(false)
  const [eForm, setEForm] = useState<{ fullName: string; email: string; role: StaffRole; status: StaffStatus }>({ fullName: '', email: '', role: 'waiter', status: 'ACTIVE' })
  const [busy, setBusy] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // pin reset
  const [pinOpen, setPinOpen] = useState(false)
  const [rPinId, setRPinId] = useState(''); const [rPin, setRPin] = useState(''); const [pinError, setPinError] = useState<string | null>(null)
  // PIN de gerente (override): vacio = no se toca; casilla marcada = se borra.
  const [rMgrPin, setRMgrPin] = useState(''); const [rMgrClear, setRMgrClear] = useState(false)
  const [revealedPin, setRevealedPin] = useState<{ name: string; pinId: string | null; pin: string | null } | null>(null)

  // activar/suspender
  const [toggling, setToggling] = useState(false)

  const u = user.data
  const roleLabel = useMemo(() => assignableRoles.find((r) => r.name === u?.role)?.label ?? (u ? users.STAFF_ROLE_LABEL[u.role as StaffRole] ?? u.role : ''), [assignableRoles, u])

  // Rol asignado (objeto Role completo, para listar sus módulos en SOLO LECTURA).
  const assignedRole = useMemo<Role | null>(() => rolesList.data?.find((r) => r.name === u?.role) ?? null, [rolesList.data, u])
  const grantedModules = useMemo(() => new Set(assignedRole?.modules ?? []), [assignedRole])

  function openEdit() {
    if (!u) return
    setEForm({ fullName: u.fullName, email: u.email ?? '', role: u.role as StaffRole, status: u.status === 'DELETED' ? 'SUSPENDED' : u.status })
    setEditError(null)
    setEditing(true)
  }
  async function doEdit() {
    if (!u) return
    setBusy(true); setEditError(null)
    try {
      await users.updateUser(u.id, { fullName: eForm.fullName.trim(), email: eForm.email.trim() || undefined, role: eForm.role, status: eForm.status })
      toast('Usuario actualizado', 'success')
      setEditing(false)
      user.reload()
    } catch (e) { setEditError(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }

  function openPin() {
    if (!u) return
    setRPinId(u.pinId ?? ''); setRPin(''); setRMgrPin(''); setRMgrClear(false); setPinError(null); setPinOpen(true)
  }
  async function doPin() {
    if (!u) return
    setBusy(true); setPinError(null)
    try {
      const r = await users.setUserPin(u.id, rPinId, rPin || undefined, rMgrClear ? '' : (rMgrPin || undefined))
      setPinOpen(false)
      setRevealedPin({ name: u.fullName, pinId: r.pinId, pin: rPin || null })
      user.reload()
    } catch (e) { setPinError(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }

  async function toggleActive() {
    if (!u) return
    setToggling(true)
    try { await users.setUserActive(u.id, u.status !== 'ACTIVE'); user.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo cambiar el estado', 'danger') }
    finally { setToggling(false) }
  }

  async function remove() {
    if (!u) return
    if (!confirm(`¿Eliminar a ${u.fullName}? Esto libera su ID de POS.`)) return
    try { await users.deleteUser(u.id); toast('Usuario eliminado', 'success'); navigate('/users') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }

  function copy(text: string) { void navigator.clipboard?.writeText(text) }

  const kpis = useMemo(() => {
    if (!u) return []
    const items: { label: string; value: React.ReactNode; tone?: StatusTone }[] = [
      { label: 'Rol', value: roleLabel || '—' },
      { label: 'ID de POS', value: u.pinId ?? '—', tone: u.pinId ? 'info' : 'neutral' },
      { label: 'PIN de gerente', value: u.hasManagerPin ? 'Configurado' : 'Sin configurar', tone: u.hasManagerPin ? 'info' : 'neutral' },
    ]
    return items
  }, [u, roleLabel])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/users')} aria-label="Volver a personal"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Usuario</h1><p className="adm-page__sub">Personal · ficha del usuario</p></div>
        </div>
      </div>

      {asyncState(user.loading, user.error, user.reload)}

      {u && (
        <EntityDetailPage
          title={u.fullName || 'Sin nombre'}
          subtitle={[u.email || 'sin correo', roleLabel, u.pinId ? `ID de POS ${u.pinId}` : 'sin ID de POS'].filter(Boolean).join(' · ')}
          pills={<>
            <StatusPill tone={STATUS_TONE[u.status]} label={STATUS_LABEL[u.status]} />
            {!u.email && <StatusPill tone="neutral" label="Sin acceso al admin" />}
          </>}
          kpis={<SummaryStrip items={kpis} />}
          actions={<>
            <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit} disabled={u.status === 'DELETED'}><Pencil size={15} /> Editar</button>
            {u.status !== 'DELETED' && (
              <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={toggleActive} disabled={toggling} title={u.status === 'ACTIVE' ? 'Suspender' : 'Activar'}>
                <Power size={15} /> {u.status === 'ACTIVE' ? 'Suspender' : 'Activar'}
              </button>
            )}
            <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={openPin} disabled={u.status === 'DELETED'}><KeyRound size={15} /> Reiniciar PIN</button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={remove} disabled={u.status === 'DELETED'}><Trash2 size={15} /> Eliminar</button>
          </>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'roles', label: 'Roles/Permisos', count: assignedRole ? grantedModules.size : undefined },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del usuario"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre completo">{u.fullName || <span className="adm-muted">—</span>}</Field>
              <Field label="Correo">{u.email || <span className="adm-muted">Sin correo (no entra al admin)</span>}</Field>
              <Field label="Rol">{roleLabel || <span className="adm-muted">—</span>}</Field>
              <Field label="ID de POS"><span className="adm-mono">{u.pinId || '—'}</span></Field>
              <Field label="Estado"><StatusPill tone={STATUS_TONE[u.status]} label={STATUS_LABEL[u.status]} /></Field>
            </div>
          )}

          {tab === 'roles' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
                <Field label="Rol asignado">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {roleLabel || <span className="adm-muted">—</span>}
                    {assignedRole && <span className={`adm-badge ${assignedRole.builtin ? 'adm-badge--info' : 'adm-badge--ok'}`}>{assignedRole.builtin ? 'Integrado' : 'Personalizado'}</span>}
                  </span>
                </Field>
                <Field label="Cambiar de rol">
                  <span className="adm-muted">Usa <strong>Editar</strong> para reasignar el rol de este usuario.</span>
                </Field>
              </div>

              <div className="adm-card adm-card--pad0">
                {asyncState(rolesList.loading, rolesList.error, rolesList.reload)}
                {!rolesList.loading && !assignedRole && (
                  <div className="adm-empty">No se encontró la definición del rol “{u.role}”. Puede ser un rol retirado; reasigna uno desde Editar.</div>
                )}
                {!rolesList.loading && assignedRole && grantedModules.size === 0 && (
                  <div className="adm-empty">Este rol no tiene módulos asignados. El usuario no verá secciones hasta darle un rol con módulos.</div>
                )}
                {!rolesList.loading && assignedRole && grantedModules.size > 0 && (
                  <div className="adm-list">
                    <div className="adm-row">
                      <div className="adm-row__main">
                        <div className="adm-row__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ShieldCheck size={14} aria-hidden /> Módulos que puede ver</div>
                        <div className="adm-row__meta">Definidos por el rol “{roleLabel}” (solo lectura — edítalos en Roles).</div>
                      </div>
                    </div>
                    {MODULE_GROUPS.map((g) => {
                      const mods = g.mods.filter((m) => grantedModules.has(m.key))
                      if (mods.length === 0) return null
                      return (
                        <div key={g.app}>
                          <div className="adm-row" style={{ background: 'var(--pos-elevated)' }}>
                            <div className="adm-row__main"><div className="adm-row__meta" style={{ textTransform: 'uppercase', letterSpacing: 'var(--pos-tracking-wide, 0.04em)', fontWeight: 600 }}>{g.label}</div></div>
                          </div>
                          {mods.map((m) => (
                            <div key={m.key} className="adm-row">
                              <div className="adm-row__main">
                                <div className="adm-row__title">{m.label}</div>
                                <div className="adm-row__meta">{m.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={u.id} />}
        </EntityDetailPage>
      )}

      {/* Editar usuario — MISMO formulario que el modal de edición de la lista */}
      {u && (
        <SectionedSheet
          open={editing}
          title={`Editar a ${u.fullName}`}
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={doEdit} disabled={busy || !eForm.fullName.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          {editError && <div className="adm-banner adm-banner--danger" role="alert">{editError}</div>}
          <FormSection title="Identidad">
            <label className="adm-field"><span className="adm-field__label">Nombre completo</span><input className="adm-input" value={eForm.fullName} onChange={(e) => setEForm({ ...eForm, fullName: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">Correo</span><input className="adm-input" type="email" value={eForm.email} onChange={(e) => setEForm({ ...eForm, email: e.target.value })} placeholder="solo si entrará al admin" /></label>
          </FormSection>
          <FormSection title="Acceso">
            <label className="adm-field"><span className="adm-field__label">Rol</span><select className="adm-select" value={eForm.role} onChange={(e) => setEForm({ ...eForm, role: e.target.value as StaffRole })}>{assignableRoles.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}</select></label>
            <label className="adm-field"><span className="adm-field__label">Estado</span><select className="adm-select" value={eForm.status} onChange={(e) => setEForm({ ...eForm, status: e.target.value as StaffStatus })}><option value="ACTIVE">Activo</option><option value="SUSPENDED">Suspendido</option></select></label>
          </FormSection>
        </SectionedSheet>
      )}

      {/* Reiniciar PIN */}
      {u && (
        <Sheet open={pinOpen} title={`PIN de ${u.fullName}`} onClose={() => setPinOpen(false)}
          footer={<><button className="adm-btn" onClick={() => setPinOpen(false)} disabled={busy}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={doPin} disabled={busy || rPinId.length !== 6 || (!!rPin && rPin.length !== 6) || (!rMgrClear && !!rMgrPin && (rMgrPin.length < 4 || rMgrPin.length > 8))}>Guardar PIN</button></>}>
          <p className="adm-row__meta" style={{ marginBottom: 12 }}>El ID de POS son 6 dígitos; el PIN son 6. Si no cambias el PIN, se conserva el actual (o se genera uno).</p>
          <label className="adm-field"><span className="adm-field__label">ID de POS (6 dígitos)</span><input className="adm-input" inputMode="numeric" maxLength={6} value={rPinId} onChange={(e) => setRPinId(e.target.value.replace(/\D/g, ''))} /></label>
          <label className="adm-field"><span className="adm-field__label">PIN (6 dígitos, opcional)</span><input className="adm-input" type="password" inputMode="numeric" maxLength={6} value={rPin} onChange={(e) => setRPin(e.target.value.replace(/\D/g, ''))} placeholder="dejar vacío para no cambiarlo" /></label>
          {/* PIN de gerente: secreto DISTINTO del de acceso. El POS lo pide para autorizar
              overrides (forzar liberar mesa, reembolsos). Sin él, manager-verify da 401. */}
          <label className="adm-field"><span className="adm-field__label">PIN de gerente (4 a 8 dígitos, opcional)</span><input className="adm-input" type="password" inputMode="numeric" maxLength={8} value={rMgrPin} disabled={rMgrClear} onChange={(e) => setRMgrPin(e.target.value.replace(/\D/g, ''))} placeholder="dejar vacío para no cambiarlo" /></label>
          <p className="adm-row__meta" style={{ marginTop: -4, marginBottom: 10 }}>Autoriza acciones que el POS protege (forzar liberar mesa, reembolsos). Es distinto del PIN de acceso.</p>
          {u.hasManagerPin === true && (
            <label className="adm-field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={rMgrClear} onChange={(e) => { setRMgrClear(e.target.checked); if (e.target.checked) setRMgrPin('') }} />
              <span className="adm-field__label" style={{ margin: 0 }}>Quitar el PIN de gerente</span>
            </label>
          )}
          {pinError && <div className="adm-banner adm-banner--danger" role="alert">{pinError}</div>}
        </Sheet>
      )}

      {/* PIN revelado una vez */}
      <Sheet open={revealedPin != null} title="Credencial de POS" onClose={() => setRevealedPin(null)}
        footer={<button className="adm-btn adm-btn--primary" onClick={() => setRevealedPin(null)}>Entendido</button>}>
        <p className="adm-row__meta" style={{ marginBottom: 12 }}>Anota esto ahora — el PIN no se vuelve a mostrar. {revealedPin?.name} lo usará para entrar al POS.</p>
        <div className="adm-banner" style={{ background: 'var(--pos-info-bg)', color: 'var(--pos-info)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {revealedPin?.pinId && <span><strong>ID de POS:</strong> <code>{revealedPin.pinId}</code></span>}
          {revealedPin?.pin && <span><strong>PIN:</strong> <code>{revealedPin.pin}</code></span>}
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => copy(`ID de POS: ${revealedPin?.pinId ?? '—'} · PIN: ${revealedPin?.pin ?? '—'}`)}><Copy size={14} /> Copiar</button>
        </div>
      </Sheet>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
